import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows } from '@/lib/supabase/fetchAllRows';

// ---------------------------------------------------------------------------
// tisaExclusion — shared defense for direct-query services (cockpit + stats)
// that read planning.placements directly, keyed by child_id, for a
// historical or caller-selectable date window. Unlike "active as of today"
// queries (structurally clean post-migration — flagged children have zero
// active placements, see supabase/migrations/20260721040000_tisa_exclusion.sql),
// a past or caller-picked date window can still surface a TISA-flagged
// child's now-ended placement (e.g. the 24 TK->TISA transition children, or
// the 2 owner-reviewed edge cases whose placements were end-dated to
// current_date - 1). This helper cross-references children_masked (already
// filtered `where not is_tisa`) instead of requiring the `is_tisa` column
// client-side (not yet in src/integrations/supabase/types.ts — see
// docs/plans/2026-07-21-tisa-exclusion.md, Task 4).
// ---------------------------------------------------------------------------

/** Max ids per `.in()` request — above this, the visible id set is read instead (see nonTisaChildIds). */
const IN_CHUNK = 200;

/**
 * Every child id visible in children_masked (already `where not is_tisa`),
 * paged to completion. Concurrent callers share one in-flight read — a
 * cockpit load fires several nonTisaChildIds calls from independent
 * services within the same second — and the promise is dropped as soon as it
 * settles, so nothing outlives the request.
 *
 * The shared read is scoped to the signed-in account: the result depends on
 * the caller's RLS, so an account switch while a read is pending must not hand
 * the old account's id set to the new account. The service has no user
 * context of its own, so App's account-change handler (sign-out or a
 * different user id) calls resetVisibleChildIdUniverse(), which bumps
 * `accountGeneration` and detaches the pending read. A read started under an
 * earlier generation never becomes the shared read again and rejects instead
 * of resolving (its later pages may have run under the new session).
 */
let accountGeneration = 0;
let inFlightUniverse: { generation: number; promise: Promise<Set<string>> } | null = null;

/** Detach any pending shared read; call on account change (see App.tsx). */
export function resetVisibleChildIdUniverse(): void {
  accountGeneration += 1;
  inFlightUniverse = null;
}

function visibleChildIdUniverse(): Promise<Set<string>> {
  if (inFlightUniverse && inFlightUniverse.generation === accountGeneration) return inFlightUniverse.promise;
  const generation = accountGeneration;
  const promise = fetchAllRows<{ id: string }>(
    () => supabase.from('children_masked').select('id', { count: 'exact' }),
    { column: 'id' },
  )
    .then((rows) => {
      if (generation !== accountGeneration) {
        throw new Error('Visible child id read discarded: the signed-in account changed while it was pending.');
      }
      return new Set(rows.map((row) => row.id));
    })
    .finally(() => {
      if (inFlightUniverse?.promise === promise) inFlightUniverse = null;
    });
  inFlightUniverse = { generation, promise };
  return promise;
}

/**
 * Given a set of child ids (typically distinct child_ids from a
 * planning.placements read), returns the subset that are NOT flagged
 * is_tisa, verified via membership in children_masked.
 *
 * Up to IN_CHUNK ids: one `.in('id', ids)` read (children_masked.id is the
 * primary key, so it can never return more rows than ids requested).
 *
 * More than IN_CHUNK ids: several callers pass the distinct child_id set of
 * a full placements read (up to ~1,400 ids, ~50KB of GET query string), which
 * used to cost one chunked `.in()` read per 200 ids per caller. The membership
 * test against the whole visible children_masked id set is the same predicate
 * (id ∈ children_masked, under the caller's own RLS), so those calls read that
 * set once — paged via fetchAllRows, ~1,400 rows = 2 pages — and intersect.
 */
export async function nonTisaChildIds(childIds: string[]): Promise<Set<string>> {
  if (childIds.length === 0) return new Set();
  if (childIds.length <= IN_CHUNK) {
    const rows = await fetchAllRows<{ id: string }>(
      () => supabase.from('children_masked').select('id', { count: 'exact' }).in('id', childIds),
      { column: 'id' },
    );
    return new Set(rows.map((row) => row.id));
  }
  const universe = await visibleChildIdUniverse();
  return new Set(childIds.filter((id) => universe.has(id)));
}
